import {
  MousePointerClick, Wand2, CheckCircle2, Play, BarChart3,
  MonitorSmartphone, CalendarClock, Store, Home as HomeIcon,
  Timer, Repeat, Building2, CalendarRange,
  Activity, Eye, Stethoscope, FileText, ListChecks, Receipt, Camera,
  Wallet, ShieldCheck, ArrowRight, Layers,
} from "lucide-react";
import { Seo } from "@/components/common/Seo";
import { PageHero } from "@/components/common/PageHero";
import { Reveal, RevealGroup, RevealItem } from "@/components/common/Reveal";
import { SectionLabel } from "@/components/common/SectionLabel";
import { CTAButton } from "@/components/common/CTAButton";
import { SEO, REGISTER_URL } from "@/data/site";

/* 2 - INTELLIGENT INVENTORY */
const Inventory = () => (
  <section id="inventory" className="bg-paper py-24 md:py-32">
    <div className="container-px">
      <Reveal><SectionLabel>Intelligent Inventory</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          One advertiser at a time. No clutter.
        </h2>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="mt-6 max-w-2xl text-ink/60 leading-relaxed">
          AdX keeps advertising inventory structured and controlled, ensuring advertisements are scheduled sequentially rather than competing for the same screen at the same moment.
        </p>
      </Reveal>

      <Reveal delay={0.12}>
        <div className="mt-14 rounded-xl border border-ink/10 bg-white p-6 md:p-8">
          <div className="flex items-center gap-2 md:gap-3 overflow-hidden">
            {["Ad 01", "Ad 02", "Ad 03", "Ad 04"].map((a, i) => (
              <div key={a} className="flex items-center gap-2 md:gap-3 shrink-0">
                <div className="rounded-lg bg-ink text-white px-4 md:px-6 py-4 md:py-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">Advertiser</p>
                  <p className="mt-1 font-display font-700">{a}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-ink/30" />
              </div>
            ))}
            <div className="rounded-lg bg-teal text-ink px-4 md:px-6 py-4 md:py-5 shrink-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/60">Community</p>
              <p className="mt-1 font-display font-700">Notices</p>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-ink/10 pt-5">
            <p className="text-sm text-ink/60">Advertisements play one after another - never stacked, never split.</p>
            <p className="font-mono text-xs uppercase tracking-[0.16em] text-ink/45">Daily window · 06:00 – 23:30</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* 3 - HOW ADX WORKS */
const STEPS = [
  { n: "01", t: "Select", d: "Choose the residential communities where you want to advertise.", icon: MousePointerClick },
  { n: "02", t: "Create", d: "Choose your ad length, frequency and campaign duration.", icon: Wand2 },
  { n: "03", t: "Approve", d: "Upload your creative for AdX review.", icon: CheckCircle2 },
  { n: "04", t: "Run", d: "Your approved campaign is scheduled across the selected communities.", icon: Play },
  { n: "05", t: "Measure", d: "Review campaign activity, billing and reporting.", icon: BarChart3 },
];

const HowItWorks = () => (
  <section className="relative bg-ink text-white py-24 md:py-32 overflow-hidden">
    <div className="pointer-events-none absolute -top-24 right-1/4 h-80 w-80 rounded-full bg-teal/12 blur-[130px]" />
    <div className="relative container-px">
      <Reveal><SectionLabel dark>How AdX Works</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          Everything you need to run digital advertising.
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 border-t border-l border-white/10">
        {STEPS.map((s) => (
          <div key={s.n} className="border-r border-b border-white/10 p-7">
            <div className="flex items-center justify-between">
              <span className="font-display font-800 text-teal text-xl">{s.n}</span>
              <s.icon className="h-5 w-5 text-white/40" strokeWidth={1.8} />
            </div>
            <h3 className="mt-6 font-display font-700 text-lg">{s.t}</h3>
            <p className="mt-2 text-sm text-white/55 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* 4 - FOUR PLATFORM LAYERS */
const LAYERS = [
  { n: "01", name: "Screen Network", icon: MonitorSmartphone, body: "Digital screens with automated health monitoring and centralized network visibility." },
  { n: "02", name: "Campaign Engine", icon: CalendarClock, body: "Campaign scheduling, frequency management and continuous advertising playback." },
  { n: "03", name: "Advertising Marketplace", icon: Store, body: "Society selection, campaign booking, prepaid advertising and transparent billing." },
  { n: "04", name: "Community Dashboard", icon: HomeIcon, body: "Digital communication tools for residential community content and screen management." },
];

const PlatformLayers = () => (
  <section className="bg-white py-24 md:py-32 border-y border-ink/10">
    <div className="container-px">
      <Reveal><SectionLabel>The Platform</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          Four layers. Full stack.
        </h2>
      </Reveal>
      <RevealGroup className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
        {LAYERS.map((l) => (
          <RevealItem key={l.n} className="group relative bg-paper border border-ink/10 rounded-xl p-8 overflow-hidden transition-transform duration-300 hover:-translate-y-1">
            <span className="absolute -right-4 -top-6 font-display font-800 text-[7rem] leading-none text-ink/[0.04] select-none">{l.n}</span>
            <div className="relative flex items-center gap-3">
              <span className="grid place-items-center h-11 w-11 rounded-lg bg-ink text-teal">
                <l.icon className="h-5 w-5" strokeWidth={1.8} />
              </span>
              <h3 className="font-display font-700 text-xl">{l.name}</h3>
            </div>
            <p className="relative mt-6 text-ink/65 leading-relaxed">{l.body}</p>
          </RevealItem>
        ))}
      </RevealGroup>
    </div>
  </section>
);

/* 5 - CAMPAIGN CONTROL */
const CONTROLS = [
  { icon: Timer, t: "Ad Length", d: "Choose from 5–30 seconds, in 5-second increments." },
  { icon: Repeat, t: "Frequency", d: "Play every 1, 2, 3 or 5 minutes." },
  { icon: Building2, t: "Societies", d: "Select one or multiple residential societies." },
  { icon: CalendarRange, t: "Duration", d: "Pick a predefined campaign duration, or set a custom one." },
];

const CampaignControl = () => (
  <section className="relative bg-ink text-white py-24 md:py-32 overflow-hidden">
    <div className="pointer-events-none absolute bottom-0 left-1/4 h-72 w-72 rounded-full bg-teal/12 blur-[130px]" />
    <div className="relative container-px">
      <Reveal><SectionLabel dark>Campaign Control</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          Your campaign. Your control.
        </h2>
      </Reveal>
      <RevealGroup className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-xl overflow-hidden">
        {CONTROLS.map((c) => (
          <RevealItem key={c.t} className="bg-ink p-7">
            <c.icon className="h-7 w-7 text-teal" strokeWidth={1.6} />
            <h3 className="mt-6 font-display font-700 text-lg">{c.t}</h3>
            <p className="mt-3 text-sm text-white/55 leading-relaxed">{c.d}</p>
          </RevealItem>
        ))}
      </RevealGroup>
    </div>
  </section>
);

/* 6 - MULTI-SOCIETY CAMPAIGNS */
const MultiSociety = () => (
  <section className="bg-paper py-24 md:py-32">
    <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5">
        <Reveal><SectionLabel>Multi-Society Campaigns</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display font-800 text-4xl md:text-5xl tracking-tight text-balance">
            One campaign. Multiple communities.
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-6 text-ink/60 leading-relaxed max-w-md">
            Select multiple residential communities and manage them through one campaign configuration and one unified campaign experience.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="lg:col-span-7">
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-center">
          <div className="sm:col-span-4 rounded-xl bg-ink text-white p-6">
            <Layers className="h-6 w-6 text-teal" strokeWidth={1.6} />
            <p className="mt-4 font-display font-700 text-lg">One campaign configuration</p>
            <p className="mt-2 text-sm text-white/50">Length · frequency · duration · creative</p>
          </div>
          <div className="hidden sm:flex sm:col-span-1 items-center justify-center">
            <ArrowRight className="h-5 w-5 text-ink/30" />
          </div>
          <div className="sm:col-span-7 grid grid-cols-1 gap-3">
            {["Society A", "Society B", "Society C"].map((s) => (
              <div key={s} className="flex items-center gap-3 rounded-lg border border-ink/10 bg-white p-4">
                <Building2 className="h-5 w-5 text-teal" strokeWidth={1.7} />
                <span className="font-display font-600">{s}</span>
                <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-ink/40">[editable]</span>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* 7 - MONITORING */
const MONITOR = [
  { icon: Activity, t: "Automated Monitoring", d: "Screen health is tracked across the network so issues can be surfaced quickly." },
  { icon: Eye, t: "Live Network Visibility", d: "A clear, real-time view of which screens are online and delivering." },
  { icon: Stethoscope, t: "Screen-Level Diagnostics", d: "Per-screen status that helps keep campaign delivery reliable." },
];

const Monitoring = () => (
  <section className="relative bg-ink text-white py-24 md:py-32 overflow-hidden">
    <div className="pointer-events-none absolute top-1/3 right-0 h-80 w-80 rounded-full bg-teal/12 blur-[130px]" />
    <div className="relative container-px">
      <Reveal><SectionLabel dark>Monitoring</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          Your campaigns are continuously monitored.
        </h2>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="mt-6 max-w-2xl text-white/65 leading-relaxed">
          AdX automatically monitors screen health across the network, helping identify disruptions and maintain reliable campaign delivery.
        </p>
      </Reveal>
      <RevealGroup className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {MONITOR.map((m) => (
          <RevealItem key={m.t} className="border border-white/10 rounded-xl bg-white/[0.03] p-8">
            <m.icon className="h-7 w-7 text-teal" strokeWidth={1.6} />
            <h3 className="mt-6 font-display font-700 text-xl">{m.t}</h3>
            <p className="mt-3 text-white/55 leading-relaxed">{m.d}</p>
          </RevealItem>
        ))}
      </RevealGroup>
    </div>
  </section>
);

/* 8 - REPORTING */
const REPORTS = [
  { icon: BarChart3, t: "Campaign Reporting" },
  { icon: Building2, t: "Society-wise Reporting" },
  { icon: ListChecks, t: "Impression Records" },
  { icon: Receipt, t: "Billing Records" },
  { icon: FileText, t: "Campaign Status" },
  { icon: Camera, t: "Playback Evidence" },
];

const Reporting = () => (
  <section className="bg-white py-24 md:py-32 border-y border-ink/10">
    <div className="container-px">
      <Reveal><SectionLabel>Reporting & Transparency</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          Know where your advertising went.
        </h2>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="mt-6 max-w-2xl text-ink/60 leading-relaxed">
          AdX maintains detailed campaign and advertising records, giving advertisers visibility into campaign activity and billing.
        </p>
      </Reveal>
      <RevealGroup className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10 rounded-xl overflow-hidden">
        {REPORTS.map((r) => (
          <RevealItem key={r.t} className="bg-paper p-7 group transition-colors duration-300 hover:bg-white">
            <r.icon className="h-7 w-7 text-teal" strokeWidth={1.6} />
            <h3 className="mt-5 font-display font-700 text-lg">{r.t}</h3>
          </RevealItem>
        ))}
      </RevealGroup>
    </div>
  </section>
);

/* 9 - PLATFORM VS MEDIA DESK */
const COMPARE = [
  ["Campaign booking", "Manual coordination", "Digital campaign booking"],
  ["Society selection", "Manual", "Select communities digitally"],
  ["Frequency", "Fixed inventory", "Configurable frequency"],
  ["Creative approval", "Manual", "Digital approval workflow"],
  ["Billing", "Manual reconciliation", "Automated billing records"],
  ["Campaign reporting", "Post-campaign", "Detailed activity reports"],
  ["Screen monitoring", "Limited", "Automated monitoring"],
];

const VsMediaDesk = () => (
  <section className="bg-ink text-white py-24 md:py-32">
    <div className="container-px">
      <Reveal><SectionLabel dark>Positioning</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          A platform, not a media desk.
        </h2>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="mt-6 max-w-2xl text-white/65 leading-relaxed">
          AdX brings campaign planning, booking, scheduling, billing and reporting into one digital advertising platform.
        </p>
      </Reveal>
      <div className="mt-14 overflow-hidden rounded-xl border border-white/10">
        <div className="grid grid-cols-12 bg-white/[0.04] font-mono text-xs uppercase tracking-[0.16em]">
          <div className="col-span-4 p-4 md:p-5 text-white/70">Capability</div>
          <div className="col-span-4 p-4 md:p-5 text-white/40">Traditional Media Buying</div>
          <div className="col-span-4 p-4 md:p-5 text-teal">AdX Platform</div>
        </div>
        {COMPARE.map((row, i) => (
          <div key={i} className="grid grid-cols-12 items-center border-t border-white/10">
            <div className="col-span-4 p-4 md:p-5 font-display font-600 text-sm md:text-base">{row[0]}</div>
            <div className="col-span-4 p-4 md:p-5 text-white/45 text-sm">{row[1]}</div>
            <div className="col-span-4 p-4 md:p-5 text-white text-sm font-medium">{row[2]}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* 10 - WALLET */
const WALLET = ["Prepaid Balance", "Campaign Charges", "Wallet Transactions", "Campaign Controls"];

const WalletSection = () => (
  <section className="bg-paper py-24 md:py-32">
    <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5">
        <Reveal><SectionLabel>Prepaid Campaigns</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display font-800 text-4xl md:text-5xl tracking-tight text-balance">
            Prepaid. Controlled. Transparent.
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-6 text-ink/60 leading-relaxed max-w-md">
            Manage campaign spending through a prepaid AdX wallet. Campaign activity and associated charges are recorded transparently.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="lg:col-span-7">
        <div className="rounded-xl border border-ink/10 bg-white p-6 md:p-8">
          <div className="flex items-center gap-3 border-b border-ink/10 pb-5">
            <span className="grid place-items-center h-11 w-11 rounded-lg bg-ink text-teal"><Wallet className="h-5 w-5" strokeWidth={1.7} /></span>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/45">AdX Wallet</p>
              <p className="font-display font-700 text-lg">Prepaid balance</p>
            </div>
            <span className="ml-auto font-display font-800 text-2xl text-ink">₹ <span className="text-ink/30">[balance]</span></span>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-px bg-ink/10 rounded-lg overflow-hidden">
            {WALLET.map((w) => (
              <div key={w} className="bg-paper p-5">
                <p className="font-display font-600">{w}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* 11 - CREATIVE APPROVAL */
const CreativeApproval = () => (
  <section className="bg-ink text-white py-24 md:py-28">
    <div className="container-px grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
      <div>
        <SectionLabel dark>Creative Approval</SectionLabel>
        <h2 className="mt-6 font-display font-800 text-3xl md:text-4xl tracking-tight text-balance">
          Every creative is reviewed.
        </h2>
        <p className="mt-6 text-white/65 leading-relaxed max-w-lg">
          Upload your advertisement and submit it for review. Approved creatives become eligible for campaign scheduling. If a creative needs changes, it can be revised and resubmitted.
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        {[["Upload", Camera], ["Submit for review", ArrowRight], ["Approved", CheckCircle2], ["Eligible to schedule", Play]].map(([t, Icon], i) => (
          <div key={t} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-5 py-4">
            <span className="font-mono text-xs text-teal">{String(i + 1).padStart(2, "0")}</span>
            <Icon className="h-4 w-4 text-white/60" />
            <span className="font-display font-600">{t}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* 13 - SECURITY */
const Security = () => (
  <section className="bg-white py-24 md:py-28 border-y border-ink/10">
    <div className="container-px grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
      <div>
        <ShieldCheck className="h-8 w-8 text-teal" strokeWidth={1.5} />
        <h2 className="mt-6 font-display font-800 text-3xl md:text-4xl tracking-tight text-balance">
          Security & transparency by default.
        </h2>
      </div>
      <ul className="space-y-4">
        {[
          "No unnecessary personal data collection",
          "Automated + manual creative review",
          "GST-compliant billing",
          "Full audit trail on financial transactions",
        ].map((t) => (
          <li key={t} className="flex gap-3 text-ink/70 border-b border-ink/10 pb-4">
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-teal shrink-0" /> {t}
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* 14 - FINAL CTA */
const FinalCta = () => (
  <section className="bg-paper py-24 md:py-32">
    <div className="container-px">
      <div className="rounded-2xl bg-ink text-white p-10 md:p-16 relative overflow-hidden">
        <div className="pointer-events-none absolute -bottom-24 -right-10 h-72 w-72 rounded-full bg-teal/15 blur-[120px]" />
        <div className="relative max-w-2xl">
          <h2 className="font-display font-800 text-4xl md:text-5xl tracking-tight text-balance">
            Ready to put AdX to work for your brand?
          </h2>
          <p className="mt-5 text-white/65 leading-relaxed">
            Plan, book, schedule and measure residential digital advertising - all in one platform.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <CTAButton href={REGISTER_URL} variant="primary" data-testid="platform-final-start">Start Advertising</CTAButton>
            <CTAButton to="/contact" variant="ghostLight" data-testid="platform-final-talk">Talk to AdX</CTAButton>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default function Platform() {
  return (
    <>
      <Seo title={SEO.platform.title} description={SEO.platform.description} />
      <PageHero
        label="Platform & Technology"
        titleLines={["Built for Performance.", "Designed for Control."]}
        accentIndex={1}
        subtitle="AdX is a full-stack digital advertising platform built to manage campaigns, screen inventory, scheduling, billing and reporting across residential communities."
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <CTAButton href="#inventory" variant="primary" data-testid="platform-hero-explore">Explore the Platform</CTAButton>
          <CTAButton href={REGISTER_URL} variant="ghostLight" data-testid="platform-hero-start">Start Advertising</CTAButton>
        </div>
      </PageHero>
      <Inventory />
      <HowItWorks />
      <PlatformLayers />
      <CampaignControl />
      <MultiSociety />
      <Monitoring />
      <Reporting />
      <VsMediaDesk />
      <WalletSection />
      <CreativeApproval />
      <Security />
      <FinalCta />
    </>
  );
}
